// VocabularyFieldDrawer.tsx · v12.10.28 (#2209 "Their id" column no longer offered) · 2026-11-02 · VoiceSpectrum AI
// VocabularyFieldDrawer.tsx · v12.10.10 (#1406 the List box - any field, platform or the practice's own, is Tied to a list + Column; the offer token is named) · 2026-10-08 · VoiceSpectrum AI
// VocabularyFieldDrawer.tsx · v12.10.8 (#1227 On / Off - Off hides the field from screens and pickers, calls still store it;
//   #1190 Identity extraction is gone - Raw saves as "raw", the text no longer mentions identity extraction) · 2026-10-05
// VocabularyFieldDrawer.tsx — v12.9.27 (#1045 Date rules / Time rules box under Type; #1054 Type shows the type the
//   engine really uses - a date-of-birth field is `birthdate`, which was missing from FIELD_TYPES, so the select
//   fell back to its first option and read "Text")
// VocabularyFieldDrawer.tsx — v12.4.16 (#499)
//
// ONE DRAWER, TWO ENTRY POINTS. NOT TWO DRAWERS THAT DRIFT.
//
// Tushar, 2026-09-19: "we can call the same drawer from identity to here." The Add Identifier drawer under
// Settings › AI Agent › Identity extraction has always asked Name / Key / Type / Links to / Entity field, and
// its help text has always said "The stored token becomes {entity.field}" — which is, word for word, what
// Vocabulary now needs in order to let a practice add subject.accountNumber without waiting for a build.
//
// So the markup moved HERE out of AiAgentSections.tsx and both screens render this. Copying it would have
// produced the failure this repo keeps re-finding under different numbers: one screen fixed, its twin not
// (#296 three token lists that disagreed; #405 twelve editors of which six were wired). A shared component
// cannot half-drift.
//
// WHAT THE TWO MODES ACTUALLY DIFFER BY, and it is less than it looks:
//   identity    Name · Key · Type · Links to (entity) · Entity field (PICKED from fields that exist)
//               v12.6.0 (C1): it used to add Parse strategy + On parse failure through `extra`, on the
//               reasoning that "how SPEECH becomes a value is what Identity extraction is for". That
//               reasoning was sound and the controls were not: the CallEngine has zero references to
//               IdentityIdentifier, so neither had ever run. The job they claimed is done by the
//               field's declared TYPE, in the engine. See AiAgentSections.tsx for the full note.
//   vocabulary  Name · Key · Type · Links to (entity) · Entity field (TYPED, because it does not exist yet)
//               + Source — how the field gets filled, the rule from BDR-001 v1.2 §7.3 that makes tenant
//               extension safe. A tenant may only create a field the platform can actually fill.
//
// Everything else — the scrim, the accent, the head, the field stack, the footer, the disabled-until-named
// Create button — is one definition. The drawer CSS lives under `.ai-agent-panel` in pages/ai-agent.css, so
// this component brings that class with it and looks identical wherever it is opened. (T2 has no route-level
// code splitting, so that stylesheet is present on every route.)
import { ReactNode } from 'react';
import { DateRulesBox, DateRule, DatePurposeInfo, DateOrderInfo, isDateLikeType, purposeFor } from './DateRulesBox';

// The one shape both entry points edit. Identity's stored row carries more (strategy / onFail / isOn /
// sortOrder); it keeps those in its own state and hands this drawer only the part the two screens share.
export interface VocabFieldDraft {
  name: string;
  key: string;
  type: string;
  entity?: string;
  field?: string;
  source?: string;      // vocabulary mode only (#499)
  listKey?: string;     // vocabulary mode only (#500): the List a `choice` field is BACKED BY
  listColumn?: string;  // v12.10.10 (#1406): which column of the list - name | description | id | custom1 | custom2
  cleanup?: string;     // v12.7.3 (#641): '' = use the organization default | 'off' | 'fast' | 'ai'
  dateRule?: DateRule | null;   // v12.9.27 (#1045): vocabulary mode, Date / Time fields only
  datePurpose?: string;         // v12.9.27 (#1045): served by the API for a platform field ("BirthDate" ...)
  isOn?: boolean;               // v12.10.8 (#1227): Off = hidden from display only
}

export type DrawerMode = 'identity' | 'vocabulary';

// v12.4.16 (#518/#499): THE TYPE SET IS THE CATALOG'S. FieldTypes in VocabularyCatalog.cs declares
// text | name | date | time | number | phone | email | zip | choice, and this is that list in the tenant's
// words. It supersedes the drawer's old seven (which had `custom`, meaning "we did not say" — normalised to
// text server-side) and the Organization controller's old four. One list, three consumers.
// `choice` is offered because #500 defines it; until a field is actually backed by a List it behaves as text,
// and the hint below says so rather than implying validation that does not run yet.
export const FIELD_TYPES: [string, string][] = [
  ['text', 'Text'],
  ['name', 'Name — spelled back for confirmation'],
  ['date', 'Date'],
  ['time', 'Time'],
  ['number', 'Number'],
  ['phone', 'Phone'],
  ['email', 'Email'],
  ['zip', 'ZIP / Postal'],
  ['choice', 'Choice — one entry from a List'],
];

// v12.4.16 (#499): BDR-001 v1.2 §7.3. Only these three. Computed and column are platform-only and are
// deliberately absent rather than present-and-disabled: a tenant cannot add a column, and there is no code to
// run for a computed field nobody wrote, so offering them at all would be offering a token that never fills —
// the exact failure v1.1 §6.3 banned tenant extension to prevent.
export const FIELD_SOURCES: [string, string, string][] = [
  ['captured', 'Asked on the call', 'The AI asks for it and stores what the caller answers.'],
  ['external', 'Returned by a system', 'Filled from a connected system’s response, through an action’s mapping.'],
  ['list', 'Chosen from a List', 'One entry from a List you maintain under Lists.'],
];

export function VocabularyFieldDrawer(props: {
  mode: DrawerMode;
  /** null = the drawer is closed. */
  draft: VocabFieldDraft | null;
  editing: boolean;
  /** Entities the field can hang off, as [token, the tenant's word]. */
  entities: [string, string][];
  /** identity mode: the existing fields to pick from, as [key, label]. Ignored in vocabulary mode. */
  fields?: [string, string][];
  /** vocabulary mode (#500): the tenant's Lists, as [key, name]. Offered when the type is `choice`. */
  lists?: [string, string][];
  /** identity mode only: the two parse controls, rendered by the caller that owns them. */
  extra?: ReactNode;
  /** Shown under the Key box when the key would collide — the caller owns uniqueness, it knows the registry. */
  keyError?: string;
  /**
   * v12.7.0 (#576): open a PLATFORM field to be read, not edited.
   *
   * Tushar, having verified 12.6.2: *"every field now opens inline, wasn't it supposed to open drawer?"* — and
   * he was right. #576's own fix shape said the field opens THIS component, "the same component, not a copy
   * (#499's rule)". I built an inline card instead because read-only meant disabling every control, and made
   * that call silently. Two components for one concept is the shape that produced #428, #429, #437, #439 and
   * #440, so it is this drawer, with everything disabled and the reason said out loud.
   *
   * A platform field's name and type are platform-managed (BDR-001 v1.2 §7.6, BDR-013 Decision 6). Being able
   * to SEE the type is the whole ask: since #563 it is what the engine validates a live answer against, and
   * since #569 it decides whether a caller is re-asked at all.
   */
  readOnly?: boolean;
  /** v12.9.27 (#1045): what the platform offers per date purpose, and the read-only date order. */
  datePurposes?: DatePurposeInfo[];
  dateOrder?: DateOrderInfo;
  saving?: boolean;
  onChange: <K extends keyof VocabFieldDraft>(k: K, v: VocabFieldDraft[K]) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const { mode, draft, editing, entities, fields = [], lists = [], extra, keyError, saving, readOnly = false, datePurposes = [], dateOrder, onChange, onClose, onSave } = props;

  // ── v12.8.0 (#659): READ-ONLY WAS TOO WIDE. ───────────────────────────────────────────────────────────
  //
  // #576 opened a platform field with EVERY control disabled and cited BDR-001 v1.2 section 7.6 for all of
  // them. Section 7.6 lists three things: the {entity.field} token grammar, key uniqueness, and reserved
  // prefixes. It does not mention the label - and section 7.1 says the opposite outright, that a default
  // label "is tenant-renameable like every other label". The project's own summary of BDR-001 is: token is
  // the stable key, label is the tenant's word.
  //
  // Tushar, 2026-09-23: "only key and type should not be edited by tenant. everything else name, input
  // cleanup, should be editable." That is narrower than what shipped and it matches the written decisions
  // better than what shipped did.
  //
  // So `locked` is the real constraint - KEY and TYPE - and `readOnly` now only means "this is a platform
  // field": it still hides Create/Cancel in favour of Close, and it still greys the structural controls.
  // Type stays locked by BDR-013 Decision 6, which IS about type: it is what the engine checks a caller's
  // answer against on a live call, so one mis-set dropdown would break a standard field for every call.
  const locked = readOnly;
  if (!draft) return null;

  const isVocab = mode === 'vocabulary';
  // v12.10.8 (#1227): the fields the Contacts card always needs.
  const alwaysOn = ['subject', 'caller', 'patient'].includes((draft.entity ?? '').toLowerCase()) && ['firstname', 'lastname', 'phone'].includes((draft.field || draft.key || '').toLowerCase());
  const token = draft.entity && (draft.field || draft.key)
    ? `${draft.entity}.${draft.field || draft.key}`
    : (draft.key || '');
  // Vocabulary mode needs an entity to hang the field off; identity mode has always allowed "(none)".
  // v12.4.16 (#500): a `choice` field that names no List is a dropdown with nothing in it, so it cannot be
  // created. Saying so here is the difference between a field that does not work yet and one that never will.
  const needsList = isVocab && draft.type === 'choice' && !(draft.listKey ?? '').trim();
  const listLabel = (lists.find(([v]) => v === (draft.listKey ?? ''))?.[1]) ?? '';
  // v12.9.27 (#1054): a type the tenant cannot pick (a platform-only one such as `birthdate`) is still SHOWN as
  // what it is, never as the select's first option. Date of birth reads as a Date - UI-LOCK-12.9.27 v1.6.
  const PLATFORM_ONLY_TYPES: Record<string, string> = { birthdate: 'Date' };
  const typeListed = FIELD_TYPES.some(([v]) => v === draft.type);
  const showRules = isVocab && isDateLikeType(draft.type);
  const canSave = !!draft.name.trim() && !keyError && (!isVocab || !!draft.entity) && !needsList;

  return (
    <div className="ai-agent-panel">
      <div className="aa-drawer-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="aa-drawer" role="dialog" aria-modal="true" aria-label={editing ? 'Edit field' : 'Add field'}>
          <div className="dw-accent" />
          <div className="dw-head">
            <div>
              <h4>{readOnly ? 'Field' : (editing ? (isVocab ? 'Edit field' : 'Edit identifier') : (isVocab ? 'Add field' : 'Add identifier'))}</h4>
              <p>{readOnly
                ? 'A field the platform manages. Its KEY and TYPE are fixed — the key is what workflows and integrations bind to, and the type is what the call engine checks a caller’s answer against on a live call. Its NAME, Input cleanup and rules within the platform’s range are yours.'
                : (isVocab
                  ? 'A field of your own on an existing vocabulary entity. It becomes a token you can use in workflow cards, integration mappings and Contacts.'
                  : 'A value the AI captures from the caller, then passes to integrations and the workspace.')}</p>
            </div>
            <button className="aa-modal-x" type="button" onClick={onClose} aria-label="Close">&#10005;</button>
          </div>

          <div className="dw-body">
            <div className="aa-f">
              <label>Name</label>
              <input value={draft.name} onChange={(e) => onChange('name', e.target.value)}
                placeholder={isVocab ? 'e.g. Account number' : 'e.g. Date of birth'} />
              <span className="aa-hint">What you call it on screen. Renaming it later never rewrites anything already stored.</span>
            </div>

            <div className="aa-f">
              <label>Key</label>
              <input value={draft.key} onChange={(e) => onChange('key', e.target.value)} disabled={locked}
                placeholder={isVocab ? 'accountNumber' : 'date_of_birth'} />
              <span className="aa-hint">Letters, numbers and underscores. This is the part after the dot and it never changes &mdash; workflows bind to it.</span>
              {keyError ? <span className="aa-hint aa-hint-err">{keyError}</span> : null}
            </div>

            <div className="aa-f">
              <label>Type</label>
              <select value={draft.type} onChange={(e) => onChange('type', e.target.value)} disabled={locked}>
                {!typeListed && draft.type ? <option value={draft.type}>{PLATFORM_ONLY_TYPES[draft.type] ?? draft.type}</option> : null}
                {FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <span className="aa-hint">What the value <em>is</em>. It decides how what the caller said is tidied up, and the format it travels in to another system.</span>
            </div>

            {/* v12.9.27 (#1045): DATE RULES / TIME RULES, right under Type (UI-LOCK-12.9.27 v1.6). Editable on a
                platform field too - the rules are the tenant's, within the platform's range. */}
            {showRules && (
              <DateRulesBox
                fieldKey={draft.entity ? `${draft.entity}.${draft.field || draft.key}` : draft.key}
                type={draft.type}
                purpose={purposeFor(draft.type, draft.datePurpose)}
                purposes={datePurposes}
                order={dateOrder}
                value={draft.dateRule}
                onChange={(r) => onChange('dateRule', r)}
              />
            )}

            {/* ── v12.7.3 (#641): INPUT CLEANUP, PER FIELD. UI-LOCKED section 3 puts it directly under Type,
                and that is the right neighbour: the TYPE is what a cleanup is checked against.

                Tushar, 2026-09-23: *"input cleanup can stay on workflow step, didn't we keep it on fields also.
                so whatever is defined at field level will be shown in workflow and if required to change on
                workflow, it can be changed to a different kind cleanup."* So this is the MIDDLE of a chain -
                step overrides field, field overrides organization - and the default here is deliberately
                "use the organization default" so an existing field behaves exactly as it does today.

                AI assist is greyed on a TEXT field because there is no shape to check an invention against:
                the guard that makes AI safe on a typed field ("does it still satisfy the declared type?")
                cannot be asked of free text. That is the "Take with it" -> "Take with food" case. */}
            <div className="aa-f">
              <label>Input cleanup</label>
              <select value={draft.cleanup === 'off' ? 'raw' : (draft.cleanup ?? '')} onChange={(e) => onChange('cleanup', e.target.value)}>
                <option value="">Use organization default</option>
                <option value="raw">Raw &mdash; no processing</option>
                <option value="fast">Fast &mdash; deterministic</option>
                <option value="ai" disabled={draft.type === 'text'}>AI assist{draft.type === 'text' ? ' \u2014 not available on text' : ''}</option>
              </select>
              <span className="aa-hint">
                {draft.type === 'text'
                  ? 'AI assist is unavailable on a text field: with no declared shape, an invented value cannot be told apart from a real one. Raw or Fast only.'
                  : 'Raw stores exactly what the phone system heard. Fast tidies it in-app. AI assist runs Fast first, then the model, and keeps the model\u2019s answer only if it still matches the type above.'}
              </span>
            </div>

            {/* v12.4.16 (#500): FORK A, decided by Tushar ("for A yes, that would be beneficial") - a List
                attaches to a FIELD, not to an Integrations mapping table, so one definition drives the workflow
                dropdown, the validation of the caller's answer and the id sent to each system. Only the first of
                those three runs today; see the Lists section's own note. */}
            {/* v12.10.10 (#1406, UI-PROTO-T47-LIST-DRAWERS v1.0 #3): ANY field - a platform one too - can be tied to a list and
                a column. What the caller says is matched to the list; {lists.entity.field} in a prompt offers the entries; at
                send time the column goes out for the system being called. Platform fields: only this box and the label are yours. */}
            {isVocab && (
              <div className="aa-f vf-listbox">
                <label>List</label>
                <div className="vf-listrow" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 10 }}>
                  <div>
                    <span className="aa-sub">Tied to list</span>
                    <select value={draft.listKey ?? ''} onChange={(e) => { onChange('listKey', e.target.value); if (e.target.value && !draft.listColumn) onChange('listColumn', 'name'); }}>
                      <option value="">&mdash; none &mdash;</option>
                      {lists.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <span className="aa-sub">Column</span>
                    <select value={draft.listKey ? (draft.listColumn || 'name') : ''} disabled={!draft.listKey} onChange={(e) => onChange('listColumn', e.target.value)}>
                      {!draft.listKey && <option value="">&mdash;</option>}
                      <option value="name">Name</option>
                      <option value="description">Description</option>
                      {/* v12.10.28 (#2209): "Their id" is gone - ids are the external ID tokens, set in Integrations › Translations.
                          A field still tied to it shows the old value so the practice can re-tie it. */}
                      {draft.listColumn === 'id' && <option value="id">Their id (removed - use Translations)</option>}
                      <option value="custom1">Custom field 1</option>
                      <option value="custom2">Custom field 2</option>
                    </select>
                  </div>
                </div>
                <span className="aa-hint">
                  {lists.length === 0
                    ? 'You have no lists yet. Create one under Settings \u203a Vocabulary \u203a Lists first.'
                    : draft.listKey
                      ? <>What the caller says is matched to {listLabel || 'this list'}. Use <code>{`{lists.${token}}`}</code> in a prompt to offer the entries; at send time each connected system gets its own value for this column.</>
                      : 'Tie this field to a list to match what callers say and send each system its own value.'}
                </span>
                {needsList ? <span className="aa-hint aa-hint-err">Pick the list this field chooses from.</span> : null}
              </div>
            )}

            {isVocab && (
              <div className="aa-f">
                <label>Filled by</label>
                <select value={draft.source ?? 'captured'} onChange={(e) => onChange('source', e.target.value)} disabled={readOnly}>
                  {FIELD_SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <span className="aa-hint">{FIELD_SOURCES.find(([v]) => v === (draft.source ?? 'captured'))?.[2]}</span>
              </div>
            )}

            <div className="aa-f">
              <label>{isVocab ? 'Add it to (Vocabulary entity)' : 'Links to (Vocabulary entity)'}</label>
              <select value={draft.entity ?? ''} onChange={(e) => onChange('entity', e.target.value)} disabled={readOnly}>
                {entities.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <span className="aa-hint">Ties this field to a Vocabulary entity so the workflow token, its display label, and the AI read-back all stay in sync.</span>
            </div>

            {!isVocab && (
              <div className="aa-f">
                <label>Entity field</label>
                <select value={draft.field ?? ''} onChange={(e) => onChange('field', e.target.value)} disabled={readOnly}>
                  {fields.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <span className="aa-hint">The stored token becomes {'{entity.field}'} &mdash; e.g. subject.firstName &mdash; while it displays with your Vocabulary label.</span>
              </div>
            )}

            {extra}

            {/* v12.10.8 (#1227): ON / OFF. Tushar 10/5 - the switch Identity extraction had, on every field, "it allows to
                clear clutter in display". Off HIDES the field (Contacts, the token and Stores pickers); calls still store it
                and workflows already using the token keep working. First name, Last name and Phone are always on. */}
            {isVocab && (
              <div className="aa-f">
                <label>Show on screens</label>
                <div className="aa-onoff-row">
                  <button type="button" className={`aa-onoff${draft.isOn === false ? ' off' : ''}`} aria-pressed={draft.isOn !== false}
                    disabled={alwaysOn} onClick={() => onChange('isOn', draft.isOn === false)}>
                    {draft.isOn === false ? 'Off' : 'On'}
                  </button>
                  <span className="aa-hint">{alwaysOn
                    ? 'Always on - Contacts needs it.'
                    : draft.isOn === false
                      ? 'Off - hidden from Contacts and the token pickers. Calls still store it; workflows already using it keep working.'
                      : 'On - shown in Contacts and offered in the token pickers.'}</span>
                </div>
              </div>
            )}

            {/* The token this will be, spelled out. The drawer's help text has promised "{entity.field}" since
                #235 and never showed the result; in vocabulary mode the tenant is INVENTING that token, so
                seeing it before pressing Create is the difference between a considered key and a typo. */}
            {token ? <p className="aa-hint aa-token-preview">Token: <code>{`{${token}}`}</code></p> : null}
          </div>

          <div className="dw-foot">
            {readOnly ? (
              <>
                {/* v12.8.0 (#659): a platform field has two editable properties now, so it needs a Save. */}
                <button className="aa-mc" type="button" onClick={onClose}>Close</button>
                <button className="aa-ma" type="button" onClick={onSave} disabled={!!saving}>{saving ? 'Saving…' : 'Save'}</button>
              </>
            ) : (
              <>
                <button className="aa-mc" type="button" onClick={onClose}>Cancel</button>
                <button className="aa-ma" type="button" onClick={onSave} disabled={!canSave || !!saving}>
                  {saving ? 'Saving…' : (editing ? 'Save' : 'Create')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
